import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Button, List, Tag, Space, Empty, message } from 'antd';
import { MessageOutlined, EyeOutlined, CloseOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { notificationsApi } from '../../utils/api';
import { toPersianDigits } from '../../utils/formatPriceToman';

const POLL_INTERVAL_MS = 25000;
const SHOWN_KEY = 'crm_unread_sms_shown_ids';
const NOTIFICATION_SOUND = '/images/mixkit-software-interface-start-2574.wav';

const readShownKeys = () => {
  try {
    const raw = sessionStorage.getItem(SHOWN_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
};

const writeShownKeys = (set) => {
  try {
    sessionStorage.setItem(SHOWN_KEY, JSON.stringify([...set]));
  } catch {
    // ignore
  }
};

const resolveSmsPath = (item) => {
  if (!item) return null;
  const nav = item.navigation || {};
  const screen = nav.screen;
  const entityId = nav.entity_id ?? item.deal_id ?? item.lead_id ?? item.client_id;
  const tab = nav.tab || 'sms-history';

  if (screen === 'sms_inbox') return '/sms/unmatched';
  if (screen === 'deal_detail' || (entityId != null && item.deal_id && !screen)) {
    const id = nav.entity_id ?? item.deal_id;
    if (id != null) return `/deals/${id}?tab=${encodeURIComponent(tab)}`;
  }
  if (screen === 'lead_detail') {
    const id = nav.entity_id ?? item.lead_id;
    if (id != null) return `/leads/${id}?tab=${encodeURIComponent(tab)}`;
  }
  if (screen === 'client_detail') {
    const id = nav.entity_id ?? item.client_id;
    if (id != null) return `/clients/${id}?tab=${encodeURIComponent(tab)}`;
  }
  // fallback از فیلدهای سطح ریشه
  if (item.deal_id) return `/deals/${item.deal_id}?tab=sms-history`;
  if (item.lead_id) return `/leads/${item.lead_id}?tab=sms-history`;
  if (item.client_id) return `/clients/${item.client_id}?tab=sms-history`;
  return '/sms/unmatched';
};

const getNotificationKey = (item) => String(item?.id ?? `${item?.from_number}-${item?.received_at}`);

/**
 * پاپ‌آپ پیامک‌های ورودی جدید — polling هر ~۲۵ ثانیه
 */
const SmsNotificationPoller = () => {
  const { isAuthenticated, token } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [actingId, setActingId] = useState(null);
  const shownRef = useRef(readShownKeys());
  const audioRef = useRef(null);

  const playSound = useCallback(() => {
    try {
      if (!audioRef.current) {
        audioRef.current = new Audio(NOTIFICATION_SOUND);
        audioRef.current.volume = 0.45;
      }
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    } catch {
      // ignore
    }
  }, []);

  const poll = useCallback(async () => {
    if (!isAuthenticated || !token) return;
    try {
      const res = await notificationsApi.getUnreadSms();
      const list = Array.isArray(res?.notifications)
        ? res.notifications
        : Array.isArray(res?.data?.notifications)
          ? res.data.notifications
          : Array.isArray(res?.data)
            ? res.data
            : [];

      const fresh = list.filter((item) => {
        const key = getNotificationKey(item);
        return key && !shownRef.current.has(key);
      });

      if (!fresh.length) return;

      fresh.forEach((item) => shownRef.current.add(getNotificationKey(item)));
      writeShownKeys(shownRef.current);

      setItems((prev) => {
        const merged = [...fresh, ...prev];
        const seen = new Set();
        return merged.filter((item) => {
          const key = getNotificationKey(item);
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      });
      setOpen(true);
      playSound();
    } catch (error) {
      // silent — polling نباید UX را مختل کند
      console.warn('Unread SMS poll failed:', error?.message || error);
    }
  }, [isAuthenticated, token, playSound]);

  useEffect(() => {
    if (!isAuthenticated || !token) return undefined;
    poll();
    const timer = setInterval(poll, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [isAuthenticated, token, poll]);

  const markRead = async (item) => {
    if (!item?.id) return;
    try {
      await notificationsApi.markAsRead(item.id);
    } catch (error) {
      console.warn('Mark SMS notification read failed:', error);
    }
  };

  const removeItem = (item) => {
    const key = getNotificationKey(item);
    setItems((prev) => {
      const next = prev.filter((x) => getNotificationKey(x) !== key);
      if (!next.length) setOpen(false);
      return next;
    });
  };

  const handleDismiss = async (item) => {
    setActingId(getNotificationKey(item));
    await markRead(item);
    removeItem(item);
    setActingId(null);
  };

  const handleDismissAll = async () => {
    setActingId('all');
    await Promise.all(items.map((item) => markRead(item)));
    setItems([]);
    setOpen(false);
    setActingId(null);
  };

  const handleView = async (item) => {
    setActingId(getNotificationKey(item));
    await markRead(item);
    const path = resolveSmsPath(item);
    removeItem(item);
    setActingId(null);
    if (path) {
      navigate(path);
    } else {
      message.warning('مسیر مشاهده پیامک یافت نشد');
    }
  };

  if (!isAuthenticated || !token) return null;

  return (
    <Modal
      open={open && items.length > 0}
      title={
        <span className="custom-font flex items-center gap-2">
          <MessageOutlined className="text-sky-500" />
          پیامک جدید
          <Tag color="blue" className="custom-font m-0">
            {items.length}
          </Tag>
        </span>
      }
      onCancel={handleDismissAll}
      footer={
        <Space className="custom-font">
          <Button onClick={handleDismissAll} loading={actingId === 'all'}>
            رد کردن همه
          </Button>
        </Space>
      }
      width={480}
      destroyOnClose={false}
      maskClosable={false}
    >
      {items.length === 0 ? (
        <Empty className="custom-font" />
      ) : (
        <List
          className="custom-font"
          dataSource={items}
          renderItem={(item) => {
            const key = getNotificationKey(item);
            const title =
              item.heading ||
              (item.sender_name ? `پیامک جدید از ${item.sender_name}` : 'پیامک جدید');
            return (
              <List.Item
                className="!px-0"
                actions={[
                  <Button
                    key="dismiss"
                    size="small"
                    icon={<CloseOutlined />}
                    className="custom-font"
                    loading={actingId === key}
                    onClick={() => handleDismiss(item)}
                  >
                    رد کردن
                  </Button>,
                  <Button
                    key="view"
                    type="primary"
                    size="small"
                    icon={<EyeOutlined />}
                    className="custom-font bg-sky-600 hover:bg-sky-700 border-0"
                    loading={actingId === key}
                    onClick={() => handleView(item)}
                  >
                    مشاهده تاریخچه
                  </Button>,
                ]}
              >
                <List.Item.Meta
                  title={<span className="custom-font text-sm font-semibold">{title}</span>}
                  description={
                    <div className="custom-font text-xs text-slate-600 dark:text-slate-300 space-y-1">
                      {item.from_number ? (
                        <div>شماره فرستنده: {toPersianDigits(String(item.from_number))}</div>
                      ) : null}
                      <div className="line-clamp-3 text-slate-700 dark:text-slate-200">
                        {item.message || '—'}
                      </div>
                    </div>
                  }
                />
              </List.Item>
            );
          }}
        />
      )}
    </Modal>
  );
};

export default SmsNotificationPoller;
